// Lazy-loads the Google Maps JS API (geometry + marker libraries) once.
// NOTE: the "drawing" library / DrawingManager was removed in Maps JS v3.65,
// so we no longer request it — polygon drawing is done manually in CutMap.
//
// IDENTICAL COPY in dashboard/, portal/ and app/. The three apps share no package, so this is
// duplicated rather than imported; change one and change all three.
//
// THE MEMO CACHES A SUCCESS, NEVER A FAILURE.
//
// This used to hold onto the promise unconditionally, which meant one transient stumble — a slow
// network, a dropped script request, a blip — left a REJECTED promise in the module for the life of
// the page. Every later caller got that same rejection back, so every map on every tab stayed dead
// until the operator happened to do a full reload, and no amount of navigating fixed it. On
// 2026-08-10 that read as "the staging maps are broken", then as "they fixed themselves", and sent
// us looking at the Maps key referrer allowlist, which was correct all along.
//
// Clearing the memo on rejection makes the next caller try again. This is the same failure shape as
// the app_build id cached before its transaction committed
// (docs/decisions/2026-08-10-cache-only-committed-ids.md): a cached failure with no route back,
// where the retry is the thing that would have fixed it and the cache is what prevents the retry.
let promise: Promise<any> | null = null;

export function loadGoogleMaps(): Promise<any> {
  if (promise) return promise;
  const attempt: Promise<any> = new Promise((resolve, reject) => {
    const w = window as any;
    if (w.google?.maps) return resolve(w.google);
    const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;
    if (!key) return reject(new Error("VITE_GOOGLE_MAPS_API_KEY not set"));

    // WHY A CALLBACK AND NOT `onload`.
    //
    // Google warns in the console when the API is loaded without `loading=async`. But adding that
    // parameter changes the contract: the bootstrap completes AFTER the script's own load event, so
    // `onload` fires while `google.maps.Map`, `google.maps.marker` and `google.maps.LatLngBounds`
    // do not exist yet. Every caller here takes the resolved value and immediately reaches for
    // `google.maps.*`, so resolving from `onload` would silence the warning by breaking every map
    // on all three surfaces.
    //
    // The documented pairing is `callback=`: Google invokes it only once the core API AND the
    // libraries named in `libraries=` are ready. That preserves the old contract exactly, so no
    // call site changes.
    const cb = "__oratorGmapsReady";
    const cleanup = () => { try { delete w[cb]; } catch { w[cb] = undefined; } };
    // THE CALLBACK IS NOT THE FINISH LINE (2026-09-25).
    //
    // `callback=` fires when the core API and the libraries named in `libraries=` are ready. That
    // is NOT the same as the map renderer being ready, and the gap is where the dashboard's blank
    // maps came from. A map constructed in that window comes up in RASTER mode with an unresolved
    // cloud style: it draws nothing, throws nothing, warns nothing. Measured on staging 2026-09-25
    // by counting canvases -- the real cut map had five 320x320 raster tile canvases and was blank;
    // a map built by hand seconds later in the same page had ONE WebGL canvas and rendered. Same
    // key, same Map ID, same options. The only difference was how early it was built.
    //
    // It also explains why the phone was never affected: a canvasser lands on a list, moves around,
    // and opens a map long after the library has settled. The dashboard's cut screen builds its map
    // the instant the loader returns, which is the worst possible moment.
    //
    // `importLibrary("maps")` is the documented way to wait for the real thing. Awaiting it here
    // means no call site has to know about any of this.
    w[cb] = () => {
      cleanup();
      const g = w.google;
      if (g?.maps?.importLibrary) g.maps.importLibrary("maps").then(() => resolve(g), () => resolve(g));
      else resolve(g);
    };

    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}`
          + `&libraries=geometry,marker&v=quarterly&loading=async&callback=${cb}`;
    s.async = true;
    // onerror still covers the cases the callback never reaches: a bad key, a referrer the key does
    // not allow, or the network being gone. Those never invoke the callback at all.
    //
    // NAME THE LIKELY CAUSE FIRST. This message said "(check key / referrer restriction)" and said
    // nothing about the network -- two of the three causes, and not the one that actually happens.
    // On 2026-09-17 a canvasser on one bar read it on a doorstep and it sent us auditing a Google
    // Cloud key that was fine; the map came back by itself on wifi minutes later. A message a
    // canvasser reads in the field should name what is true in the field.
    s.onerror = () => { cleanup(); reject(new Error("Google Maps did not load — most often no signal, sometimes a bad key or a referrer the key does not allow")); };
    document.head.appendChild(s);
  });
  // Keep it only while it is still viable: a resolved memo is the whole point (one script tag per
  // page), a rejected one is a permanently broken map surface.
  promise = attempt;
  attempt.catch(() => { if (promise === attempt) promise = null; });
  return attempt;
}
